/**
 * Catálogo de planos — fonte única: API /plans (Configuração → Financeiro → Planos).
 * O Proposal Studio só referencia planos daqui; escopo nunca vem de template legado da proposta.
 */
import type { PropostaItem, PropostaItemTipo } from '@/types'
import { findCategoryCoords } from '@/utils/dealCalculations'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { normalizeInitialLayout } from '@/components/Deals/ProposalStudio/normalizeInitialLayout'
import {
  getBuiltinPlanCategoryCoords,
  getBuiltinPlanSlideLayout,
  lockedRocketGalaxyLayout,
} from '@/utils/planLayoutDefaults'
import { normalizeObjectEncoding, normalizeTextEncoding } from '@/utils/text-normalization'
import {
  rebuildCategoryCoordsFromScope,
  type ScopeCategoryInput,
} from '@/components/Deals/ProposalStudio/layoutSlideBlocks'

export interface CatalogPlan {
  id: string
  nome: string
  /** Descrição comercial do plano (campo description da API) */
  descricaoPlano: string
  valor: number
  tipo: PropostaItemTipo
  recorrente: boolean
  config?: Record<string, unknown>
  category?: string
}

export function mapApiPlanToCatalog(p: Record<string, unknown>): CatalogPlan {
  const typeRaw = (p.type || p.tipo || 'plano') as string
  const tipo: PropostaItemTipo =
    typeRaw === 'RECORRENTE' || typeRaw === 'plano'
      ? 'plano'
      : typeRaw === 'servico' || typeRaw === 'PACOTE_HORAS' || typeRaw === 'DEMANDAS_ESPECIFICAS'
        ? 'servico'
        : 'avulso'

  return normalizeObjectEncoding({
    id: String(p.id || ''),
    nome: normalizeTextEncoding(String(p.name || p.nome || '')),
    descricaoPlano: normalizeTextEncoding(String(p.description || p.descricao || '')),
    valor: Number(p.baseValue ?? p.valorMensal ?? p.valor ?? 0),
    tipo,
    recorrente: typeRaw === 'RECORRENTE' || p.recorrente !== false,
    config: (p.config as unknown as Record<string, unknown>) || {},
    category: String(p.category || 'Marketing'),
  })
}

/** Planos Rocket Search — exclusivos do produto Rocket, não entram em proposta de deal CRM. */
export type PlanCatalogScope = 'deals' | 'rocket'

export function isGalaxyPlanLabel(name?: string | null): boolean {
  return (name ?? '').toLowerCase().includes('galaxy')
}

export function isRocketCatalogPlan(plan: {
  nome?: string
  name?: string
  category?: string
  config?: object | null
  raw?: { name?: string; category?: string; config?: object | null }
}): boolean {
  const config = (plan.config ?? plan.raw?.config) as { catalogScope?: unknown } | null | undefined
  if (config && config.catalogScope === 'rocket') return true
  const name = (plan.nome || plan.name || plan.raw?.name || '').toLowerCase()
  const category = (plan.category || plan.raw?.category || '').toLowerCase()
  return name.includes('rocket') || category.includes('rocket')
}

export function filterPlansByCatalogScope<T extends Parameters<typeof isRocketCatalogPlan>[0]>(
  plans: T[],
  scope: PlanCatalogScope = 'deals',
): T[] {
  if (scope === 'rocket') {
    return plans.filter((plan) => isRocketCatalogPlan(plan))
  }
  return plans.filter((plan) => !isRocketCatalogPlan(plan))
}

/** Propostas em /deals — exclui planos Rocket. */
export function filterDealsProposalCatalogPlans<T extends Parameters<typeof isRocketCatalogPlan>[0]>(
  plans: T[],
): T[] {
  return filterPlansByCatalogScope(plans, 'deals')
}

/** Propostas em /rocket-search — somente planos Rocket. */
export function filterRocketProposalCatalogPlans<T extends Parameters<typeof isRocketCatalogPlan>[0]>(
  plans: T[],
): T[] {
  return filterPlansByCatalogScope(plans, 'rocket')
}

export function findCatalogPlanForItem(
  item: PropostaItem,
  catalog: CatalogPlan[],
): CatalogPlan | undefined {
  const planId = item.catalogPlanId
  if (planId) {
    const byId = catalog.find((p) => p.id === planId)
    if (byId) return byId
  }

  const itemName = normalizeTextEncoding(item.descricao || item.nome || item.name || '')
    .toLowerCase()
    .trim()
  if (!itemName) return undefined

  const exact = catalog.find(
    (p) => normalizeTextEncoding(p.nome).toLowerCase().trim() === itemName,
  )
  if (exact) return exact

  return catalog.find((p) => {
    const planName = normalizeTextEncoding(p.nome).toLowerCase().trim()
    return planName.includes(itemName) || itemName.includes(planName)
  })
}

function catalogCategoryIsDesenvolvimento(category?: string): boolean {
  return normalizeTextEncoding(category || '')
    .toLowerCase()
    .trim() === 'desenvolvimento'
}

/** Heurística alinhada a syncProposalItemFromCatalog / detectTemplateType. */
function planTextSignalsDesenvolvimento(...parts: (string | undefined)[]): boolean {
  const text = parts
    .filter((p): p is string => Boolean(p?.trim()))
    .join(' ')
    .toLowerCase()
  if (!text.trim()) return false
  if (text.includes('desenvolvimento') || text.includes('plano de desenvolvimento')) return true
  if (text.includes('site institucional') || text.includes('site-institucional')) return true
  if (
    text.includes('e-commerce personalizado') ||
    text.includes('desenvolvimento e-commerce') ||
    text.includes('ecommerce')
  ) {
    return true
  }
  if (text.includes('site')) return true
  return false
}

/** Plano da categoria Desenvolvimento (catálogo ou heurística por nome/escopo). */
export function isDesenvolvimentoPlan(
  plan: PropostaItem,
  catalogPlans?: CatalogPlan[],
): boolean {
  if (catalogPlans?.length) {
    const master = findCatalogPlanForItem(plan, catalogPlans)
    if (master) return catalogCategoryIsDesenvolvimento(master.category)
  }

  return planTextSignalsDesenvolvimento(
    plan.descricao,
    plan.nome,
    plan.name,
    plan.description,
    plan.projectType,
  )
}

const PLAN_LAYOUT_SLIDE_KEYS = [
  'leftBlockWidth',
  'titleY',
  'projectTypeY',
  'descriptionY',
  'supportHoursY',
  'bonusY',
  'priceY',
  'paymentY',
  'loyaltyY',
  'negotiationY',
  'planTitleY',
  'serviceBlockX',
  'serviceBlockGap',
  'serviceBlockStartY',
] as const

/** Une mapas de coords (camadas posteriores sobrescrevem). */
export function mergeCategoryCoordMaps(
  ...layers: Array<LayoutConfig['categoryCoords'] | undefined>
): Record<string, { x: number; y: number }> {
  const merged: Record<string, { x: number; y: number }> = {}
  for (const layer of layers) {
    if (!layer) continue
    Object.assign(merged, layer)
  }
  return merged
}

/** Garante coords de catálogo/builtin no export (DB antigo costuma faltar CRM no Evolve). */
export function enrichExportLayoutConfig(
  layout: LayoutConfig,
  planLabel: string,
): LayoutConfig {
  const label = normalizeTextEncoding(planLabel).trim()
  const locked = lockedRocketGalaxyLayout(label)
  if (locked) {
    return normalizeInitialLayout({
      ...layout,
      ...locked,
      categoryCoords: { ...(locked.categoryCoords || {}) },
    }) as LayoutConfig
  }
  const builtinSlide = getBuiltinPlanSlideLayout(label)
  const builtinCoords = getBuiltinPlanCategoryCoords(label)
  return normalizeInitialLayout({
    ...builtinSlide,
    ...layout,
    categoryCoords: mergeCategoryCoordMaps(
      builtinCoords,
      builtinSlide?.categoryCoords,
      layout.categoryCoords,
    ),
  }) as LayoutConfig
}

/** Layout padrão do slide (blocos fixos + categorias) salvo no plano ou builtin. */
export function getProposalLayoutDefaultsFromCatalog(master: CatalogPlan): Partial<LayoutConfig> {
  const cfg = master.config || {}
  const fromConfig = cfg.proposalLayout as unknown as Record<string, unknown> | undefined
  const out: Partial<LayoutConfig> = {}

  if (fromConfig) {
    for (const key of PLAN_LAYOUT_SLIDE_KEYS) {
      const value = fromConfig[key]
      if (typeof value === 'number' && Number.isFinite(value)) {
        ;(out as Record<string, number>)[key] = value
      }
    }
  }

  out.categoryCoords = getProposalCategoryCoordsFromCatalog(master)

  const builtinSlide = getBuiltinPlanSlideLayout(master.nome)
  if (builtinSlide) {
    for (const key of PLAN_LAYOUT_SLIDE_KEYS) {
      const value = builtinSlide[key]
      if (typeof value === 'number' && Number.isFinite(value) && out[key] === undefined) {
        ;(out as Record<string, number>)[key] = value
      }
    }
    out.categoryCoords = mergeCategoryCoordMaps(out.categoryCoords, builtinSlide.categoryCoords)
  }

  const locked = lockedRocketGalaxyLayout(master.nome)
  if (locked) {
    return {
      ...out,
      ...locked,
      categoryCoords: locked.categoryCoords ? { ...locked.categoryCoords } : out.categoryCoords,
    }
  }

  return out
}

/** Layout padrão do slide salvo no plano (Config) ou builtin por nome. */
export function getProposalCategoryCoordsFromCatalog(
  master: CatalogPlan,
): LayoutConfig['categoryCoords'] | undefined {
  const cfg = master.config || {}
  const fromConfig = cfg.proposalLayout as { categoryCoords?: LayoutConfig['categoryCoords'] } | undefined
  const builtin = getBuiltinPlanCategoryCoords(master.nome)
  const merged = mergeCategoryCoordMaps(fromConfig?.categoryCoords, builtin)
  return Object.keys(merged).length > 0 ? merged : undefined
}

/**
 * Assinatura do escopo incluindo textos (detecta mudança de capitalização/nomes no plano).
 */
export function getScopeContentSignature(serviceCategories: ScopeCategoryInput[]): string {
  return JSON.stringify(
    serviceCategories.map((cat) => ({
      cat: cat.category || cat.categoria || '',
      items: (cat.services || [])
        .filter((s) => s.isIncluded !== false)
        .map((s) => ({
          name: s.name || s.nome || '',
          qty: s.quantity,
        })),
    })),
  )
}

/**
 * Aplica coords do plano + proposta ao escopo atual (chaves = nomes do catálogo).
 * Prioridade: padrão do plano → coords da proposta (match por categoria) → empilhamento automático.
 */
export function resolveCategoryCoordsForScope(
  scope: ScopeCategoryInput[],
  layout: Pick<LayoutConfig, 'serviceBlockX' | 'serviceBlockGap' | 'serviceBlockStartY'>,
  options: {
    planDefaults?: LayoutConfig['categoryCoords']
    existing?: LayoutConfig['categoryCoords']
  } = {},
): Record<string, { x: number; y: number }> {
  const { planDefaults, existing } = options
  const merged: Record<string, { x: number; y: number }> = {}

  for (const cat of scope) {
    const name = normalizeTextEncoding((cat.category || cat.categoria || '').trim())
    const included = (cat.services || []).filter((s) => s.isIncluded !== false)
    if (!name || included.length === 0) continue

    const fromPlan = planDefaults ? findCategoryCoords(planDefaults, name) : undefined
    if (fromPlan) {
      merged[name] = fromPlan
      continue
    }

    const fromExisting = existing ? findCategoryCoords(existing, name) : undefined
    if (fromExisting) {
      merged[name] = fromExisting
    }
  }

  const missingScope = scope.filter((cat) => {
    const name = normalizeTextEncoding((cat.category || cat.categoria || '').trim())
    return name && !merged[name]
  })

  if (missingScope.length > 0) {
    const auto = rebuildCategoryCoordsFromScope(missingScope, layout)
    for (const [key, val] of Object.entries(auto)) {
      if (!merged[key]) merged[key] = val
    }
  }

  return merged
}

/** Migra chaves antigas de categoryCoords para os nomes atuais do escopo. */
export function remapCategoryCoordsToScope(
  scope: ScopeCategoryInput[],
  coords: LayoutConfig['categoryCoords'] | undefined,
): Record<string, { x: number; y: number }> {
  if (!coords) return {}
  const result: Record<string, { x: number; y: number }> = {}
  for (const cat of scope) {
    const name = normalizeTextEncoding((cat.category || cat.categoria || '').trim())
    if (!name) continue
    const found = findCategoryCoords(coords, name)
    if (found) result[name] = found
  }
  return result
}
